import { Component, OnInit, Input, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Platform } from '@ionic/angular';
import { GameStoreDispatcher } from '../../../core/state/game/game.dispatcher';
import { Observable, Subscription, Subject, timer, iif, of, from, combineLatest } from 'rxjs';
import { filter, switchMap, takeUntil, take, withLatestFrom, pluck, map, distinctUntilChanged, share } from 'rxjs/operators';
import { SmartAudioService } from 'src/app/shared/services/smart-audio/smart-audio.service';
import { GameStatus } from '@soposopo/sopo-models';
import { ProfileStateDispatcher } from 'src/app/core/state/profile/profile.dispatcher';
import { GameSocketStoreDispatcher } from 'src/app/core/state/gameSocket/gameSocket.dispatcher';
import { GameStateId } from 'src/app/core/state/game/game.state';

 export interface MyTurn {
    isDealyStarted: boolean;
    clockProgress: number;
}

type ClockProgressObservable = Observable<{timeSpentThisTurn: number, timePerTurn: number, secondsPassed: number} | null>

const DEALY_TIME = 1;

@Component({
    selector: 'player-clock',
    templateUrl: './player-clock.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./player-clock.component.scss'],
    standalone: false
})
export class PlayerClockComponent implements OnInit, OnDestroy {
    @Input() id: string;
    @Input() timePerTurn: number;
    @Input() gameStateId: GameStateId;
    @Input() tableID: string;
    @Input() isLinear: boolean = false;
    isMyTurn$: Observable<boolean>;
    myTurn$: Observable<MyTurn>;
    clientID: string;
    audioClockTimerService: Subscription;
    gameStatus: GameStatus;
    radiusValue = 70;
    readonly ONE_SECOND_IN_MS = 1000;
    ngUnsubscribe = new Subject<void>();
    constructor(
        private gameService: GameStoreDispatcher,
        private audioService: SmartAudioService,
        private profileService: ProfileStateDispatcher,
        private platform: Platform,
        private gameDispatcher: GameStoreDispatcher,
        private gameSocketDispacther: GameSocketStoreDispatcher,
    ) {
    }

    ngOnInit() {
        if (!this.id) {
            console.error(`Can't get clock for ${this.id}`);
            return;
        }
        this.getMyId();
        
        this.platform.ready().then(() => {
                this.radiusValue = 72;
        });
        this.initClock();
    }

    initClock() {
        this.isMyTurn$ = this.gameService.isPlayersTurnNotLastMan(this.id, this.gameStateId);
        const myTurnTime$ = this.initTimeObservable();
        this.createClockProgressObservable(myTurnTime$);
        if (!this.isLinear) this.initializeAudioSubscription(myTurnTime$);
    }

    initTimeObservable(): ClockProgressObservable {
        const serverTime$ = from(this.gameSocketDispacther.getGameSocketAsPromise(this.gameStateId)).pipe(
            switchMap(socket => socket.getServerTimeObservable())
        )
        const dealerTimeout$: Observable<number> = this.gameService
            .getDealer(this.gameStateId)
            .pipe(
                filter(dealer => dealer['timeout'] && dealer['timeout']['_seconds']),
                map(dealer =>  dealer['timeout']!['_seconds']),
                distinctUntilChanged(),
            )
        const dealerTime$: Observable<number> = this.gameService
            .getDealer(this.gameStateId)
            .pipe(
                filter(dealer => dealer['currentTime'] && dealer['currentTime']['_seconds']),
                map(dealer =>  dealer['currentTime']!['_seconds']),
                distinctUntilChanged()
            );
            const time$ = combineLatest([dealerTime$, serverTime$]).pipe(
            map(([dealerTime, socketTime]) => {
                if (dealerTime > socketTime) return dealerTime;
                return socketTime;
            })
        );
        return this.isMyTurn$
        .pipe(
            distinctUntilChanged(),
            switchMap((isMyTurn) => dealerTime$.pipe(
                take(1),
                switchMap((turnStartTime) => {
                    const timer$ = timer(0, this.ONE_SECOND_IN_MS).pipe(
                        withLatestFrom(time$, dealerTimeout$),
                        map(([tick, currentTime, timeout]) => ({
                            timeSpentThisTurn: currentTime - turnStartTime,
                            timePerTurn: timeout - turnStartTime,
                            secondsPassed: tick
                        }))
                    );
                    return iif(
                        () => isMyTurn,
                        timer$,
                        of(null)
                    );
                })
            )),
            share()
        );
    }

    createClockProgressObservable(progress$: ClockProgressObservable) {
        let timePerTurn: number;
        let timeSpentThisTurn: number;
        let countdownTime: number;
        let clockProgress: number;
        let isDealyStarted: boolean;
        const isTimebankRequested$ = this.gameService.getDealer(this.gameStateId)
        .pipe(map(dealer => dealer['timeAddRequested']));
        this.myTurn$ = progress$.pipe(
            withLatestFrom(isTimebankRequested$),
            map(([timeObject,isTimebankRequested]) => {
                if (timeObject === null) return {clockProgress: 0, isDealyStarted: false};
                timePerTurn = timeObject.timePerTurn;
                timeSpentThisTurn = timeObject.timeSpentThisTurn;
                if (isTimebankRequested && timeSpentThisTurn <= timePerTurn){
                    timePerTurn = timePerTurn / 2;
                    timeSpentThisTurn -= timePerTurn + DEALY_TIME;
                }
                countdownTime = timePerTurn - DEALY_TIME;
                clockProgress = (timeSpentThisTurn / countdownTime) * 100;
                isDealyStarted = (timeSpentThisTurn + (timePerTurn/10)) >= countdownTime;
                return { clockProgress, isDealyStarted }
            }),
        );
    }

    initializeAudioSubscription(progress$: ClockProgressObservable){
        const selectedGame$ = this.gameService.getState().pipe(pluck('selectedGame'));
        const isGridView$ = this.gameService.getState().pipe(pluck('gridView'));
        const selectedAndGrid$ = combineLatest([selectedGame$,isGridView$]); 
        this.audioClockTimerService = progress$
            .pipe(
                takeUntil(this.ngUnsubscribe),
                withLatestFrom(selectedAndGrid$),
            )
            .subscribe(([timeObject, [selectedGame, isGridView]]) => {
                if (timeObject === null) return;
                const timeRemaining = timeObject.timePerTurn - timeObject.timeSpentThisTurn;
                if (this.id !== this.clientID || !(selectedGame == this.gameStateId || isGridView)) return; // not client or game not active, no neded to play audio
                if (timeObject.secondsPassed === 0) this.audioService.playSounds('start');
                if (timeRemaining <= DEALY_TIME) this.audioService.playSounds('warning');
                if (timeRemaining === DEALY_TIME + 5) this.audioService.playSounds('warning'); // 5 sec remaining
            });
    }

    async getMyId() {
        this.clientID = await this.profileService.getMyID();
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
        if (this.audioClockTimerService) {
            this.audioClockTimerService.unsubscribe();
        }
    }
}
